import { ModelTransitionState } from "@gadgetinc/conventions";
import { GadgetSystemDominionId } from "../../database/DominionLiterals";
import { ApplyParamsRecordEffectSpecID, UpdateRecordEffectSpecID } from "../../database/behaviour/effects/GadgetEffectLiterals";
import { RequiredValidationId } from "../../database/validations/keys";
import { FieldType } from "../../type-system/FieldType";
import { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";
import {
  ShopifyBulkOperationCancelKey,
  ShopifyBulkOperationCanceledKey,
  ShopifyBulkOperationCompleteKey,
  ShopifyBulkOperationCompletedKey,
  ShopifyBulkOperationCreatedKey,
  ShopifyBulkOperationExpireKey,
  ShopifyBulkOperationExpiredKey,
  ShopifyBulkOperationFailKey,
  ShopifyBulkOperationFailedKey,
  ShopifyBulkOperationFinishSpecID,
  ShopifyBulkOperationKey,
  ShopifyBulkOperationToShopKey,
  ShopifyBulkOperationUpdateKey,
  ShopifyRequiredValidationKey,
  ShopifyShopKey,
} from "./keys";

export const shopifyBulkOperationTransitionState: ModelTransitionState = {
  cancel: {
    from: "created",
    to: "canceled",
  },
  complete: {
    from: "created",
    to: "completed",
  },
  create: {
    to: "created",
  },
  expire: {
    from: "created",
    to: "expired",
  },
  fail: {
    from: "created",
    to: "failed",
  },
};

const specs: ConnectionModelSpecVersion = {
  key: ShopifyBulkOperationKey,
  "2022-01": {
    name: "Shopify Bulk Operation",
    fields: [
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifyBulkOperationToShopKey,
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
        dominionID: GadgetSystemDominionId,
        contributorConfiguration: {
          [GadgetSystemDominionId]: { isWritableInternally: true, isWritablePublicly: true, storeInColumn: false },
        },
        validations: {
          [ShopifyRequiredValidationKey]: {
            key: ShopifyRequiredValidationKey,
            specID: RequiredValidationId,
          },
        },
      },
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "id" } },
      },
      {
        name: "Status",
        fieldType: FieldType.Enum,
        configuration: {
          allowMultiple: false,
          options: [
            { name: "canceled" },
            { name: "canceling" },
            { name: "completed" },
            { name: "created" },
            { name: "expired" },
            { name: "failed" },
            { name: "running" },
          ],
        },
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "status" } },
      },
      {
        name: "Error Code",
        fieldType: FieldType.Enum,
        configuration: {
          allowMultiple: false,
          options: [{ name: "access_denied" }, { name: "internal_server_error" }, { name: "timeout" }],
        },
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "errorCode" },
        },
      },
      {
        name: "Type",
        fieldType: FieldType.Enum,
        configuration: {
          allowMultiple: false,
          options: [{ name: "query" }, { name: "mutation" }],
        },
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "type" } },
      },
      {
        name: "File Size",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "fileSize" } },
      },
      {
        name: "Object Count",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "objectCount" } },
      },
      {
        name: "Partial Data URL",
        fieldType: FieldType.URL,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "partialDataUrl" } },
      },
      {
        name: "Query",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "query" } },
      },
      {
        name: "Root Object Count",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "rootObjectCount" } },
      },
      {
        name: "URL",
        fieldType: FieldType.URL,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "url" } },
      },
      {
        name: "Completed At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "completedAt" },
        },
      },
    ],
    stateChart: {
      includeCRUD: true,
      crudOptions: {
        defaultActionKeys: {
          update: ShopifyBulkOperationUpdateKey,
        },
        defaultStateKeys: {
          created: ShopifyBulkOperationCreatedKey,
        },
      },
      states: {
        _chart: [
          {
            name: "Completed",
            key: ShopifyBulkOperationCompletedKey,
            isRecordBirthPlace: false,
            isUndeleteableSystemState: true,
            color: "#67CA48",
            boxLayout: { x: 608, y: 80, width: 160, height: 48 },
          },
          {
            name: "Canceled",
            key: ShopifyBulkOperationCanceledKey,
            isRecordBirthPlace: false,
            isUndeleteableSystemState: true,
            color: "#E2CD2E",
            boxLayout: { x: 704, y: 448, width: 160, height: 48 },
          },
          {
            name: "Failed",
            key: ShopifyBulkOperationFailedKey,
            isRecordBirthPlace: false,
            isUndeleteableSystemState: true,
            color: "#E400B7",
            boxLayout: { x: 696, y: 144, width: 160, height: 48 },
          },
          {
            name: "Expired",
            key: ShopifyBulkOperationExpiredKey,
            isRecordBirthPlace: false,
            isUndeleteableSystemState: true,
            color: "#56BFCC",
            boxLayout: { x: 608, y: 512, width: 160, height: 48 },
          },
        ],
      },
      actions: [
        {
          key: ShopifyBulkOperationCompleteKey,
          name: "Complete",
          from: "created",
          to: "completed",
          onRunEffects: [
            {
              specID: ApplyParamsRecordEffectSpecID,
            },
            {
              specID: ShopifyBulkOperationFinishSpecID,
            },
            {
              specID: UpdateRecordEffectSpecID,
            },
          ],
        },
        {
          key: ShopifyBulkOperationCancelKey,
          name: "Cancel",
          from: "created",
          to: "canceled",
          onRunEffects: [
            {
              specID: ApplyParamsRecordEffectSpecID,
            },
            {
              specID: ShopifyBulkOperationFinishSpecID,
            },
            {
              specID: UpdateRecordEffectSpecID,
            },
          ],
        },
        {
          key: ShopifyBulkOperationFailKey,
          name: "Fail",
          from: "created",
          to: "failed",
          onRunEffects: [
            {
              specID: ApplyParamsRecordEffectSpecID,
            },
            {
              specID: ShopifyBulkOperationFinishSpecID,
            },
            {
              specID: UpdateRecordEffectSpecID,
            },
          ],
        },
        {
          key: ShopifyBulkOperationExpireKey,
          name: "Expire",
          from: "created",
          to: "expired",
          onRunEffects: [
            {
              specID: ApplyParamsRecordEffectSpecID,
            },
            {
              specID: ShopifyBulkOperationFinishSpecID,
            },
            {
              specID: UpdateRecordEffectSpecID,
            },
          ],
        },
      ],
    },
    syncConfig: null,
  },
  "2025-04": {
    fields: [
      {
        name: "Status",
        fieldType: FieldType.Enum,
        configuration: {
          allowMultiple: false,
          options: [
            { name: "canceled" },
            { name: "canceling" },
            { name: "completed" },
            { name: "created" },
            { name: "expired" },
            { name: "failed" },
            { name: "running" },
          ],
        },
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "status", availableInWebhook: ["bulk_operations/finish"] } },
      },
      {
        name: "Error Code",
        key: "ModelField-DataModel-Shopify-BulkOperation-errorCode",
        initialStorageEpochKey: "FieldStorageEpoch-DataModel-Shopify-BulkOperation-errorCode-initial",
        fieldType: FieldType.Enum,
        configuration: {
          allowMultiple: false,
          options: [{ name: "access_denied" }, { name: "internal_server_error" }, { name: "timeout" }],
        },
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "error_code", availableInWebhook: ["bulk_operations/finish"] },
        },
      },
      {
        name: "Type",
        fieldType: FieldType.Enum,
        configuration: {
          allowMultiple: false,
          options: [{ name: "query" }, { name: "mutation" }],
        },
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "type", availableInWebhook: ["bulk_operations/finish"] } },
      },
      {
        name: "Completed At",
        key: "ModelField-DataModel-Shopify-BulkOperation-completedAt",
        initialStorageEpochKey: "FieldStorageEpoch-DataModel-Shopify-BulkOperation-completedAt-initial",
        fieldType: FieldType.DateTime,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "completed_at", availableInWebhook: ["bulk_operations/finish"] },
        },
      },
    ],
  },
};

export const ShopifyBulkOperation = ({ apiVersion }: { apiVersion: ShopifyApiVersion }) => buildConnectionSpec(specs, apiVersion);
